import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import firebase from 'firebase/app';
import { useHistory } from 'react-router-dom';

import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import BackdropMui from '@mui/material/Backdrop';

import EnhancedTableRow from '../../components/EnhancedTableRow';
import EnhancedTableHead from '../../components/EnhancedTableHead';
import { getComparator, stableSort } from '../../modules/sort';
import LeavesListView from './LeavesListView'
import ActionDialog from '../../components/ActionDialog';

const Backdrop = styled(BackdropMui)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  color: '#fff',
}));

const statusColor = {
  pending: '#c83cc8',
  reject: '#fe2851',
  void: '#fe2851',
  done: '#00b900',
}

function LeaveList({ currentUser }) {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const [order, setOrder] = useState('desc')
  const [orderBy, setOrderBy] = useState('date')
  const [leaves, setLeaves] = useState([])
  const [dialogData, setDialogData] = useState(null);
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const unsubscribe = firebase.firestore().collection('leaves').where('createdBy', '==', currentUser.key).where('status', '!=', 'void_').onSnapshot(snapshot => {
      const companyLeaves = []
      snapshot.forEach(doc => {
        companyLeaves.push({ ...doc.data(), id: doc.id })
      });
      setLeaves(companyLeaves.filter(l => l.startDate >= `${dayjs().format('YYYY')}-01-01`))
    }, err => { })

    return () => unsubscribe()
  }, [currentUser.key]);


  const formatData = (leave) => {
    const newData = { ...leave }
    newData.start = `${newData.startDate} ${newData.startTime}`
    newData.typeName = formatMessage({ id: `leaveType.${newData.type}` })
    newData.statusName = `${formatMessage({ id: `leaveStatus.${newData.status}` })}`
    newData.end = `${newData.endDate} ${newData.endTime}`
    newData.day = `${newData.days}天${newData.hours}小時`
    newData.date = dayjs(newData.createdAt.seconds * 1000).format('YYYY-MM-DD HH:mm')
    newData.textColor = statusColor[newData.status]

    return newData
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const headerCells = [
    { text: 'type', sort: 'type' },
    { text: 'start', sort: 'start' },
    { text: 'end', sort: 'end' },
    { text: 'days', sort: 'days' },
    { text: 'date', sort: 'date' },
    { text: 'state', sort: 'status' },
  ].map(c => { c.text = formatMessage({ id: `leaves.table.${c.text}` }); return c })

  const rowCells = [
    { field: 'typeName' },
    { field: 'start' },
    { field: 'end' },
    { field: 'day' },
    { field: 'date' },
    { field: 'statusName', textColor: true },
  ]

  async function handleExecute(data) {
    const { text } = data
    const { filed, leaveId } = dialogData
    setLoading(true)

    try {
      await (firebase.functions().httpsCallable('saveLeaves'))({
        ...filed,
        id: leaveId,
        note: text,
        void: true,
      })
    } catch (ex) {
      console.log(ex)
    }

    setLoading(false)
  }

  function handleClose() {
    setDialogData(null)
  }

  return (
    <Grid container spacing={1}>
      {loading && <Backdrop open>
        <CircularProgress color="inherit" />
      </Backdrop>}
      {dialogData && <ActionDialog
        title={formatMessage({ id: `button.${dialogData.action}` })}
        handleClose={handleClose}
        handleExecute={handleExecute}
        textFieldLabel={formatMessage({ id: 'leaves.detail.voidReason' })}
        action={dialogData.action}
      />}
      <Grid item xs={12} sm={12} md={12}>
        <div style={{ paddingBottom: '60px', paddingTop: '20px' }}>
          <TableContainer component={Paper}>
            <Table aria-label="collapsible table">
              <EnhancedTableHead
                headerCells={headerCells}
                order={order}
                orderBy={orderBy}
                expandable
                onRequestSort={handleRequestSort}
                rowCount={leaves.length}
                actionButton
              />
              <TableBody>
                {stableSort(leaves.map(r => formatData(r)), getComparator(order, orderBy)).map(leave => (
                  <EnhancedTableRow
                    key={leave.id}
                    rowCells={rowCells}
                    cellData={leave}
                    expandable
                    expandContent={<LeavesListView leaves={leave} currentUser={currentUser} />}
                    actionIcons={<>
                      {(!leave.void || (leave.void && leave.status === 'reject'))&& <span>
                        <Tooltip title={formatMessage({ id: 'button.edit' })}>
                          <IconButton
                            onClick={() => history.push(`/leaveOvertimeWork/leaves/edit/${leave.id}`)}
                            size="large">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={formatMessage({ id: 'button.void' })}>
                          <IconButton
                            onClick={() => setDialogData({ action: 'void', title: '', filed: leave, leaveId: leave.id })}
                            size="large">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </span>}
                    </>}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Grid>
    </Grid>
  );
};

LeaveList.propTypes = {
  currentUser: PropTypes.shape({
    key: PropTypes.string.isRequired,
    gender: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    department: PropTypes.string.isRequired,
    isManagement: PropTypes.bool.isRequired,
    active: PropTypes.bool.isRequired,
  }),
};

export default LeaveList;
